import { Box, Center } from '@chakra-ui/react';
import { cacheImages } from 'api/cacheImages';
import { S3_IMAGE_BUCKET_LINK } from 'constants/imageUpload';
import { getInitialWidgetPosition } from 'modules/Editor/Editor.helpers';
import { addNewWidget, store } from 'modules/Editor/store';
import { selectZoom } from 'modules/Editor/store/editorSettingsSelector';
import { selectActivePage } from 'modules/Editor/store/pageSelector';
import { DragEvent } from 'react';
import { createRoot } from 'react-dom/client';
import { WidgetType } from 'types/widget.types';
import { calculateAspectRatioFit } from 'utils/calculateAspectRatioFit';
import { computeIconViewBox } from 'widgets/IconWidget/IconWidget.helpers';
import { IconWidgetData } from 'widgets/IconWidget/IconWidget.types';
import { ImageWidgetData } from 'widgets/ImageWidget/ImageWidget.types';
import { WIDGET_DRAG_DROP_IMAGE_GHOST_ID } from 'widgets/Widget.config';
import { NewWidget } from 'widgets/Widget.types';

import { CursorPosition, DropInWidgetData, PageAreaRef } from './WidgetDragDrop.types';

/**
 * Drops in the widget from drag event data onto the page canvas area, placement is based on the mouse position
 * @param {NewWidget} widgetData - widget data of the selected in widget from drop in
 * @param {PageAreaRef} pageAreaRef - ref for page container where widget is to be dropped into
 * @param {CursorPosition} cursorPosition - last cursor position of the mouse drag event
 *
 */
export const dropInWidget = async (widgetData: NewWidget, pageAreaRef: PageAreaRef, cursorPosition: CursorPosition) => {
  const storeState = store.getState();
  const zoom = selectZoom(storeState);

  const pageAreaEl = pageAreaRef?.current!;
  const { x, y } = pageAreaEl.getBoundingClientRect();
  const { heightPx, widthPx } = widgetData.widgetData;

  // Calculate center axis of the widget with respect to current zoom
  const widgetLeftCenter = widthPx * 0.5 * zoom;
  const widgetTopCenter = heightPx * 0.5 * zoom;

  // Calculate offset position from mouse cursor and center axis of the widget
  const offsetPosition = {
    topPx: (cursorPosition.y - y - widgetTopCenter) / zoom,
    leftPx: (cursorPosition.x - x - widgetLeftCenter) / zoom,
  };

  if (widgetData.widgetType === WidgetType.Image) {
    // check for image urls that need to be cached for Image Widgets
    const { shouldCache, shouldUpdateImageSize } = widgetData as DropInWidgetData;
    if (shouldCache) {
      await handleImageCaching(widgetData);
    }
    // check for images that need to have their dimensions updated for accurate aspect ratio
    if (shouldUpdateImageSize) {
      handleImageSizeUpdate(widgetData);
    }
  }

  // check for Icon Widgets that requires viewBox calculation
  if (widgetData.widgetType === WidgetType.Icon) {
    const { shouldCalculateViewBox, iconId, isIllustration } = widgetData as DropInWidgetData;

    // for icons8, we need to compute the viewBox at the end of the drop event
    if (!!iconId && shouldCalculateViewBox) {
      await handleIconViewBoxCalculation(iconId, widgetData, isIllustration);
    }
  }

  // Update widget drop in position
  widgetData.widgetData = { ...widgetData.widgetData, ...offsetPosition };

  // add widget to editor
  store.dispatch(addNewWidget(widgetData));
};

/**
 * Creates a ghost preview image of widget being dragged
 * Used in cases where native drag preview is cut off due to overflow clipping or a custom image is preferred
 * @param {DragEvent<HTMLDivElement>} e - drag event data pass through
 * @param {JSX.Element} dragImage - element to be passed as drag image
 * @param {NewWidget} widgetData - widget data of selected widget
 * @param {{x, y}} offset - x and y coordinates for the ghost image preview center axis
 *
 */
export const createGhostPreview = (
  e: DragEvent<HTMLDivElement>,
  dragImage: JSX.Element,
  widgetData: NewWidget,
  offset?: { x: number; y: number },
) => {
  const storeState = store.getState();
  const zoom = selectZoom(storeState);

  // Create ghost element
  const ghost = document.createElement('div');
  ghost.setAttribute('id', WIDGET_DRAG_DROP_IMAGE_GHOST_ID);

  // set transforms and position to outside of viewport as we only use it for the drag event, and we don't want it rendered within screen area
  ghost.style.transform = 'translate(-10000px, -10000px)';
  ghost.style.position = 'absolute';
  ghost.style.backgroundColor = 'transparent';
  ghost.style.overflow = 'visible';
  document.body.appendChild(ghost);
  const root = createRoot(ghost);

  const { widthPx: previewWidth, heightPx: previewHeight } = widgetData.widgetData;

  // center preview to center based on widget data
  // center offset is calculated from widgetData dimension (widthPx / heightPx) relative to zoom and center (0.5) of x and y-axis
  const ghostCenterOffset = offset
    ? { x: offset.x, y: offset.y }
    : {
        x: previewWidth * zoom * 0.5,
        y: previewHeight * zoom * 0.5,
      };

  // if no offset is provided, render preview dimensions based on widgetData
  const image: JSX.Element = offset ? (
    <>{dragImage}</>
  ) : (
    <Box h={`${previewHeight * zoom}px`} w={`${previewWidth * zoom}px`}>
      <Center>{dragImage}</Center>
    </Box>
  );

  // render ghost element to root
  root.render(image);

  // set the drag image to current drag event
  e.dataTransfer.setDragImage(ghost, ghostCenterOffset.x, ghostCenterOffset.y);
};

/**
 * Destroys ghost image preview created previously if it exists
 */
export const destroyGhostPreview = () => {
  const ghostImage = document.getElementById(WIDGET_DRAG_DROP_IMAGE_GHOST_ID);
  if (ghostImage) {
    ghostImage.remove();
  }
};

/**
 * Updates the image url with cached image, used for pixabay photo widgets
 *
 * @param {NewWidget} widgetData - widget data for image widget
 *
 */
const handleImageCaching = async (widgetData: NewWidget) => {
  const activePageId = selectActivePage(store.getState());
  const { url } = widgetData.widgetData as ImageWidgetData;

  try {
    const cacheImage = await cacheImages(activePageId, url);
    if (cacheImage) {
      const { url: cachedImageUrl } = cacheImage;
      widgetData.widgetData = { ...widgetData.widgetData, ...{ url: `${S3_IMAGE_BUCKET_LINK}/${cachedImageUrl}` } };
    }
  } catch (error) {
    const errorMessage = `Cannot cache the image ${url}`;
    console.error(errorMessage);
  }
};

/**
 * Updates the image sizes with natural width and height from aspect ratio fit calculation
 *
 * @param {NewWidget} widgetData - widget data for image widget
 *
 */
const handleImageSizeUpdate = (widgetData: NewWidget) => {
  const { url } = widgetData.widgetData as ImageWidgetData;
  const { infographWidth, infographHeight } = getInitialWidgetPosition();

  // create html image element with widget image url retrieved from dragStart widget data
  const imagePreview = new Image();
  imagePreview.src = url;

  // use naturalWidth and naturalHeight as basis for image widget dimension
  const { naturalWidth, naturalHeight } = imagePreview;

  // re-calculate aspect ratio fit with naturalWidth and naturalHeight from preview image element
  const { width: widthPx, height: heightPx } = calculateAspectRatioFit(
    naturalWidth,
    naturalHeight,
    infographWidth / 2,
    infographHeight / 2,
  );

  // update image widgetData with new image size
  widgetData.widgetData = {
    ...widgetData.widgetData,
    ...{
      widthPx,
      heightPx,
      imageRect: {
        widthPx: widthPx,
        heightPx: heightPx,
      },
    },
  };
};

/**
 * Calculates the viewBox for Icons8 icons
 *
 * @param {string} id - icon id
 * @param {NewWidget} widgetData - widget data for icons 8 widget
 * @param {boolean} isIllustration - flag to switch to illustration icons loader
 *
 */
const handleIconViewBoxCalculation = async (id: string, widgetData: NewWidget, isIllustration?: boolean) => {
  const viewBox = await computeIconViewBox(id, isIllustration);
  const { iconMetadata } = widgetData.widgetData as IconWidgetData;
  // update icon metadata with computed viewBox
  if (iconMetadata && !!viewBox) {
    Object.assign(iconMetadata, { viewBox });
    widgetData.widgetData = { ...widgetData.widgetData, ...{ iconMetadata } };
  }
};
